
import React, { useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HeroContent = () => {
  // Function to scroll to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  // Function to scroll to services section
  const scrollToServices = () => {
    const processSection = document.getElementById('how-we-work');
    if (processSection) {
      processSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  // Container animation variants - refined for subtlety
  const containerVariants: Variants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  // Title text animation variants with smoother reveal
  const titleVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        type: "spring",
        stiffness: 40,
        damping: 10
      }
    }
  };

  // Content animation variants - more subtle
  const contentVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.3
      }
    }
  };

  // Subtitle animation - faster reveal
  const subtitleVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 5
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.6
      }
    }
  };

  // New animation for the underline element
  const underlineVariants: Variants = {
    hidden: {
      width: 0,
      opacity: 0
    },
    visible: {
      width: "100%",
      opacity: 1,
      transition: {
        duration: 0.8,
        delay: 0.8,
        ease: "easeInOut"
      }
    }
  };

  // New animation for the icon
  const iconVariants: Variants = {
    hidden: { 
      scale: 0,
      opacity: 0
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        delay: 1
      }
    }
  };

  // Auto-focus effect
  useEffect(() => {
    const timer = setTimeout(() => {
      const focusIndicator = document.getElementById('tagline-focus');
      if (focusIndicator) {
        focusIndicator.classList.add('pulse-attention');
      }
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  return <div className="w-full mx-auto">
      <motion.div className="max-w-xl" initial="hidden" animate="visible" variants={containerVariants}>
        {/* Main heading section with tech style */}
        <motion.div className="mb-4 md:mb-5">
          <div className="mb-3 md:mb-4 inline-block overflow-hidden relative">
            <span className="absolute top-0 left-0 bg-[#FF6B6B] h-full w-1.5"></span>
            <h1 className="font-inter text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-left leading-tight tracking-tight pl-4">
              <motion.span className="block text-white font-black mb-0.5 md:mb-1 glitch-text" variants={titleVariants} custom={0}>
                Build.
              </motion.span>
              
              <motion.span className="block text-[#33C3F0] font-black mb-0.5 md:mb-1" variants={titleVariants} custom={1}>
                Launch.
              </motion.span>
              
              <motion.span className="block text-[#FF6B6B] font-black" variants={titleVariants} custom={2}>
                Scale.
              </motion.span>
            </h1>
          </div>
          
          <motion.p id="tagline-focus" className="font-inter text-lg md:text-xl text-[#FF6B6B] mt-1 md:mt-2 font-semibold relative" variants={subtitleVariants}>
            <span className="relative inline-block">
              NextGen does it all
              <motion.span 
                className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#FF6B6B] via-[#FF8C8C] to-[#FFA5A5]" 
                variants={underlineVariants}
              ></motion.span>
            </span>
            <motion.span 
              className="inline-block ml-1.5" 
              variants={iconVariants}
            >
              <Sparkles className="h-5 w-5 text-[#FFA5A5] inline" />
            </motion.span>
          </motion.p>
        </motion.div>
        
        {/* Main description with subtle texture background */}
        <motion.p 
          className="font-inter text-sm md:text-base lg:text-lg text-gray-200 mb-4 md:mb-5 leading-relaxed pl-4 border-l border-[#FF6B6B]/50 relative"
          variants={contentVariants}
        >
          <span className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik0wIDBoMXY0MEgwek0zOSAwaDFWNDBIMzl6TTAgMGg0MHYxSDB6TTAgMzloNDB2MUgweiIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIvPjwvc3ZnPg==')] opacity-50 pointer-events-none"></span>
          We help you turn your ideas into real businesses. From start to finish, we're here to make your business grow online.
        </motion.p>

        {/* Key benefits list - tech style with subtle hover */}
        <motion.div variants={contentVariants} className="mb-4 md:mb-5">
          <ul className="space-y-3">
            <li className="flex items-start bg-white/5 backdrop-blur-sm p-3 rounded-lg border-l-2 border-[#33C3F0] transition-all duration-200 hover:bg-white/8 hover:translate-x-1">
              <CheckCircle className="text-[#33C3F0] mr-2.5 h-5 w-5 mt-0.5 flex-shrink-0" />
              <span className="text-gray-200 text-sm md:text-base font-inter">Start faster - save time and money</span>
            </li>
            <li className="flex items-start bg-white/5 backdrop-blur-sm p-3 rounded-lg border-l-2 border-[#FF6B6B] transition-all duration-200 hover:bg-white/8 hover:translate-x-1">
              <CheckCircle className="text-[#FF6B6B] mr-2.5 h-5 w-5 mt-0.5 flex-shrink-0" />
              <span className="text-gray-200 text-sm md:text-base font-inter">Make your brand stand out and sell more</span>
            </li>
            <li className="flex items-start bg-white/5 backdrop-blur-sm p-3 rounded-lg border-l-2 border-[#29BF12] transition-all duration-200 hover:bg-white/8 hover:translate-x-1">
              <CheckCircle className="text-[#29BF12] mr-2.5 h-5 w-5 mt-0.5 flex-shrink-0" />
              <span className="text-gray-200 text-sm md:text-base font-inter">Get all the tools you need in one place</span>
            </li>
          </ul>
        </motion.div>
        
        {/* Call to action buttons with gradient effect */}
        <motion.div variants={contentVariants} className="flex flex-col sm:flex-row gap-4 mt-2 mb-4 w-full sm:w-auto">
          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full sm:w-auto"
          >
            <Button 
              onClick={scrollToContact} 
              size="lg" 
              className="w-full sm:w-auto font-inter py-6 px-6 rounded-md text-white bg-gradient-to-r from-[#FF4747] via-[#FF6B6B] to-[#FF8585] border border-[#FF6B6B]/50 shadow-[0_0_15px_rgba(255,107,107,0.3)] hover:shadow-[0_0_20px_rgba(255,107,107,0.4)] transition-all duration-300"
            >
              Get Started Now
              <motion.span 
                animate={{ x: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="ml-2"
              >
                <ArrowRight className="h-5 w-5" />
              </motion.span>
            </Button>
          </motion.div>
          
          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full sm:w-auto"
          >
            <Button 
              onClick={scrollToServices} 
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto font-inter py-6 px-6 rounded-md text-[#33C3F0] border-2 border-[#33C3F0]/50 bg-transparent hover:bg-[#33C3F0]/10 shadow-[0_0_10px_rgba(51,195,240,0.2)] hover:shadow-[0_0_15px_rgba(51,195,240,0.3)] transition-all duration-300"
            >
              Learn More
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* CSS for auto-focus pulse animation - fixed JSX error by removing jsx={true} prop */}
      <style>
        {`
        @keyframes pulse-focus {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0); }
          50% { box-shadow: 0 0 0 6px rgba(255, 107, 107, 0.3); }
        }
        .pulse-attention {
          animation: pulse-focus 2s ease-in-out;
        }
        `}
      </style>
    </div>;
};

export default HeroContent;
